// ─── Forfaits — provider d'autorisation (Core) ───────────────────
// Assemble le catalogue (fourni par l'app à partir de ses modules), lit la config
// d'activation partagée (Firestore config/fonctionnalites, optionnelle) et le
// profil utilisateur, et expose :
//   • peutUtiliser(cle)  — la porte (défaut PERMISSIF tant que rien n'est configuré)
//   • catalogue, defs    — le catalogue composé
//   • definirActivation(cle, actif) — active/désactive une fonction POUR l'utilisateur
//     (écrit users/{uid}.fonctionnalites[cle]) → sert à préparer/simuler les forfaits.
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import { construireCatalogue, creerPeutUtiliser } from './fonctionnalites';

const FonctionsContext = createContext(null);
export const useFonctions = () => useContext(FonctionsContext) || { peutUtiliser: () => true, catalogue: construireCatalogue([]), defs: [] };
// Raccourci le plus utilisé dans les écrans.
export const usePeutUtiliser = () => useFonctions().peutUtiliser;

export function FonctionsProvider({ catalogue: defs = [], children }) {
  const { user } = useAuth();
  const [config, setConfig] = useState({ globales: {}, plans: {}, paliers: {}, poids: {} });

  // Config d'activation partagée (admin) : un seul doc, optionnel → permissif.
  useEffect(() => {
    const ref = doc(db, 'config', 'fonctionnalites');
    const unsub = onSnapshot(ref,
      snap => { const d = snap.exists() ? snap.data() : {}; setConfig({ globales: d.globales || {}, plans: d.plans || {}, paliers: d.paliers || {}, poids: d.poids || {} }); },
      () => setConfig({ globales: {}, plans: {}, paliers: {}, poids: {} }));
    return () => unsub();
  }, []);

  const catalogue = useMemo(() => construireCatalogue(defs), [defs]);

  const peutUtiliser = useMemo(
    () => creerPeutUtiliser({ catalogue, user, globales: config.globales, plans: config.plans, paliers: config.paliers }),
    [catalogue, user, config],
  );

  // Active/désactive une fonction pour l'utilisateur courant (préparation forfaits).
  async function definirActivation(cle, actif) {
    if (!user?.uid) return;
    await setDoc(doc(db, 'users', user.uid), { fonctionnalites: { [cle]: !!actif } }, { merge: true });
  }

  const value = useMemo(() => ({
    peutUtiliser, catalogue, defs: catalogue.list, config, definirActivation,
    activationUtilisateur: user?.fonctionnalites || {},
  }), [peutUtiliser, catalogue, config, user?.fonctionnalites]);

  return <FonctionsContext.Provider value={value}>{children}</FonctionsContext.Provider>;
}
